import { useEffect, useMemo, useRef, useState } from "react";
import { User, ChevronDown, Search } from "lucide-react";
import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { formatEuro } from "@/lib/catalog-format";
import type { Product as CatalogProduct } from "@/lib/catalog";
import { POPULATED_ASSORTIMENT_CATEGORIES } from "@/lib/assortiment-categories";
import { trackNavClickFromEvent, trackCtaClick } from "@/lib/analytics";
import { UspBar } from "@/components/site/UspBar";


import elvionLogo from "@/assets/elvion-logo.png.asset.json";
import { responsiveImage } from "@/lib/responsive-image";

const elvionLogoResponsive = responsiveImage(elvionLogo.url, [160, 240, 320]);

interface TopChild {
  href: string;
  label: string;
  route?: true;
  children?: { href: string; label: string; route?: true }[];
}
interface TopLink {
  href: string;
  label: string;
  route?: true;
  children?: TopChild[];
}

const TOP_LINKS: TopLink[] = [
  {
    href: "/klantenservice",
    label: "Klantenservice",
    route: true,
  },
];

const QUICK_STRIP: { href: string; label: string }[] = [];

/**
 * Desktop + tablet header (zichtbaar vanaf `lg` — 1024px en breder).
 * Pas dit bestand aan voor desktop/tablet-specifieke wijzigingen zonder
 * de mobiele header te raken.
 */
export function HeaderDesktop() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef<HTMLFormElement>(null);
  const { session } = useAuth();

  const [catalog, setCatalog] = useState<readonly CatalogProduct[] | null>(null);
  const catalogLoadingRef = useRef(false);

  const query = searchQuery.trim().toLowerCase();
  const queryDigits = query.replace(/\D+/g, "");
  const isNumericQuery =
    queryDigits.length >= 6 && queryDigits.length === query.replace(/\s+/g, "").length;

  useEffect(() => {
    if (!query || catalog || catalogLoadingRef.current) return;
    catalogLoadingRef.current = true;
    void import("@/lib/catalog").then((m) => setCatalog(m.PRODUCTS));
  }, [query, catalog]);

  const filteredProducts = useMemo<CatalogProduct[]>(() => {
    if (!query || !catalog) return [];
    const results = catalog.filter((p) => {
      if (isNumericQuery && p.gtin && p.gtin.replace(/\D+/g, "").includes(queryDigits)) return true;
      const hay = [
        p.name,
        p.brand,
        p.category_label,
        p.spec_tag,
        p.short_description,
        p.mpn ?? "",
        p.gtin ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });
    return results.slice(0, 8);
  }, [query, queryDigits, isNumericQuery, catalog]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setShowDropdown(false);
    setHighlightedIndex(-1);
    if (searchQuery.trim()) {
      navigate({ to: "/c", search: { q: searchQuery.trim() } });
      setSearchQuery("");
    }
  };

  const handleSelectProduct = (slug: string) => {
    setShowDropdown(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
    navigate({ to: "/product/$slug", params: { slug } });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || filteredProducts.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % filteredProducts.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + filteredProducts.length) % filteredProducts.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
        handleSelectProduct(filteredProducts[highlightedIndex].slug);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  return (
    <header className="sticky top-0 z-50 hidden w-full bg-white lg:block">
      <UspBar />

      {/* Main row: logo + search + account */}
      <div className="border-b border-border/60">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex shrink-0 items-center">
            <img
              src={elvionLogoResponsive?.src ?? elvionLogo.url}
              srcSet={elvionLogoResponsive?.srcSet}
              sizes="200px"
              alt="Elvion Group Nederland"
              width={1204}
              height={328}
              className="h-10 w-auto"
              decoding="async"
            />
          </Link>

          <div className="flex flex-1 justify-center">
            <form
              onSubmit={handleSearch}
              ref={searchRef}
              className="relative flex w-full max-w-[650px] items-center"
              data-tour="search"
            >
              <div className="flex w-full items-stretch overflow-hidden rounded-md border border-border bg-white shadow-sm focus-within:border-orange focus-within:ring-2 focus-within:ring-orange/30">
                <input
                  type="text"
                  placeholder="Waar ben je naar op zoek?"
                  aria-label="Zoek in het assortiment"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowDropdown(e.target.value.trim().length > 0);
                    setHighlightedIndex(-1);
                  }}
                  onFocus={() => {
                    if (query.length > 0 && filteredProducts.length > 0) {
                      setShowDropdown(true);
                    }
                  }}
                  onKeyDown={handleSearchKeyDown}
                  className="h-11 flex-1 bg-white px-4 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
                <div className="hidden items-center border-l border-border px-3 text-sm text-foreground/70 sm:flex">
                  Alles
                  <ChevronDown className="ml-1 size-4" />
                </div>
                <button
                  type="submit"
                  aria-label="Zoeken"
                  className="flex h-11 w-12 items-center justify-center bg-orange text-white transition-colors hover:bg-orange/90"
                >
                  <Search className="size-5" />
                </button>
              </div>

              {showDropdown && filteredProducts.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-border bg-white shadow-lg">
                  <ul role="listbox" className="max-h-[360px] overflow-y-auto py-1">
                    {filteredProducts.map((p, i) => (
                      <li key={p.slug} role="option" aria-selected={i === highlightedIndex}>
                        <button
                          type="button"
                          onMouseEnter={() => setHighlightedIndex(i)}
                          onMouseLeave={() => setHighlightedIndex(-1)}
                          onClick={() => handleSelectProduct(p.slug)}
                          className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                            i === highlightedIndex ? "bg-orange/10" : "hover:bg-muted"
                          }`}
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-white">
                            {p.image_url ? (() => {
                              const r = responsiveImage(p.image_url, [80, 120]);
                              return (
                                <img
                                  src={r?.src ?? p.image_url}
                                  srcSet={r?.srcSet}
                                  sizes="80px"
                                  alt=""
                                  className="max-h-full max-w-full object-contain"
                                  loading="lazy"
                                  decoding="async"
                                />
                              );
                            })() : (
                              <Search className="size-4 text-foreground/30" />
                            )}
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-sm font-medium text-foreground">
                              {p.name}
                            </span>
                            <span className="text-xs text-text-secondary">
                              {p.category_label} • {p.spec_tag}
                            </span>
                          </div>
                          <span className="shrink-0 text-sm font-semibold text-foreground">
                            {formatEuro(p.list_price_cents)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="border-t border-border px-3 py-2 text-xs text-text-secondary">
                    {filteredProducts.length} {filteredProducts.length === 1 ? "resultaat" : "resultaten"} gevonden
                  </div>
                </div>
              )}

              {showDropdown && query.length > 0 && filteredProducts.length === 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-md border border-border bg-white shadow-lg">
                  <div className="px-4 py-3 text-sm text-text-secondary">
                    Geen producten gevonden voor "{searchQuery.trim()}"
                  </div>
                </div>
              )}
            </form>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to={session ? "/account" : "/auth"}
              data-tour="account"
              className="flex items-center gap-2 text-sm font-medium text-foreground/80 transition-colors hover:text-orange"
            >
              <User className="size-5" />
              {session ? "Account" : "Inloggen"}
            </Link>
          </div>
        </div>

        {/* Nav row */}
        <div>
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-6" onClick={(e) => trackNavClickFromEvent(e, "header_primary")}>
              <Link
                to="/"
                className={`whitespace-nowrap text-[15px] font-medium transition-colors hover:text-orange ${
                  currentPath === "/" ? "text-orange" : "text-foreground/85"
                }`}
              >
                Home
              </Link>
              <Link
                to="/elektrotechniek"
                data-tour="assortiment"
                className={`whitespace-nowrap text-[15px] font-medium transition-colors hover:text-orange ${
                  currentPath === "/elektrotechniek" ? "text-orange" : "text-foreground/85"
                }`}
              >
                Elektrotechniek
              </Link>
              <Link
                to="/beveiligingstechniek"
                className={`whitespace-nowrap text-[15px] font-medium transition-colors hover:text-orange ${
                  currentPath === "/beveiligingstechniek" ? "text-orange" : "text-foreground/85"
                }`}
              >
                Beveiligingstechniek
              </Link>
              <Link
                to="/netwerktechniek"
                className={`whitespace-nowrap text-[15px] font-medium transition-colors hover:text-orange ${
                  currentPath === "/netwerktechniek" ? "text-orange" : "text-foreground/85"
                }`}
              >
                Netwerktechniek
              </Link>
              <div className="group relative">
                <Link
                  to="/assortiment"
                  className={`relative flex items-center gap-1 whitespace-nowrap text-[15px] font-medium transition-colors hover:text-orange ${
                    currentPath === "/assortiment" ? "text-orange" : "text-foreground/85"
                  }`}
                >
                  Assortiment
                  <ChevronDown className="size-4 opacity-60" />
                </Link>
                <div className="absolute left-0 top-full z-50 hidden min-w-[220px] rounded-md border border-border bg-white py-2 shadow-lg group-hover:block">
                  {POPULATED_ASSORTIMENT_CATEGORIES.map((item) => (
                    <Link
                      key={item.slug}
                      to="/assortiment"
                      search={{ categorie: item.slug }}
                      className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-orange"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/aanbiedingen"
                className={`relative whitespace-nowrap text-[15px] font-medium transition-colors hover:text-orange ${
                  currentPath === "/aanbiedingen" ? "text-orange" : "text-foreground/85"
                }`}
              >
                <span className="absolute -top-3 right-0 rounded bg-orange px-1.5 py-0.5 text-[9px] font-bold text-white leading-none">
                  Deals
                </span>
                Aanbiedingen
              </Link>
            </nav>

            <nav className="flex items-center gap-5" onClick={(e) => trackNavClickFromEvent(e, "header_secondary")}>
              <Link
                to="/offerte-aanvragen"
                data-tour="offerte"
                className={`whitespace-nowrap text-sm transition-colors hover:text-orange ${
                  currentPath === "/offerte" ? "text-orange" : "text-foreground/70"
                }`}
              >
                Offerte
              </Link>
              <Link
                to="/advies"
                data-tour="advies"
                className="whitespace-nowrap text-sm text-foreground/70 transition-colors hover:text-orange"
              >
                Advies
              </Link>
              <Link
                to="/klantenservice"
                className="whitespace-nowrap text-sm text-foreground/70 transition-colors hover:text-orange"
                onClick={() => trackCtaClick("Contact", "/klantenservice", "sticky_header")}
              >
                Contact
              </Link>

              {TOP_LINKS.map((item) =>
                item.children ? (
                  <div key={item.label} className="group relative">
                    <Link
                      to={item.href}
                      className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground/70 transition-colors hover:text-orange"
                    >
                      {item.label}
                      <ChevronDown className="size-4 opacity-60" />
                    </Link>
                    <div className="absolute right-0 top-full z-50 hidden min-w-[180px] rounded-md border border-border bg-white py-2 shadow-lg group-hover:block">
                      {item.children.map((child) =>
                        child.children ? (
                          <div key={child.label} className="group/sub relative">
                            <div className="flex cursor-default items-center justify-between px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-orange">
                              {child.label}
                              <ChevronDown className="-rotate-90 size-3.5 opacity-60" />
                            </div>
                            <div className="absolute right-full top-0 z-50 hidden min-w-[200px] rounded-md border border-border bg-white py-2 shadow-lg group-hover/sub:block">
                              {child.children.map((g) => (
                                <Link
                                  key={g.label}
                                  to={g.href}
                                  className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-orange"
                                >
                                  {g.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Link
                            key={child.label}
                            to={child.href}
                            className="block px-4 py-2 text-sm text-foreground/80 hover:bg-muted hover:text-orange"
                          >
                            {child.label}
                          </Link>
                        ),
                      )}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="whitespace-nowrap text-sm text-foreground/70 transition-colors hover:text-orange"
                  >
                    {item.label}
                  </Link>
                ),
              )}
              <LanguageSelector />
            </nav>
          </div>
        </div>

        {/* Quick strip */}
        <div className="bg-muted/50">
          <div className="mx-auto flex max-w-7xl items-center gap-8 overflow-x-auto px-4 py-2.5 text-sm sm:px-6 lg:px-8">
            {QUICK_STRIP.map((item) => (
              <Link
                key={item.href + item.label}
                to={item.href}
                className="whitespace-nowrap transition-colors hover:text-orange text-foreground/80"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
